import { useState } from "react";
import { ChevronRight, ChevronsUpDown, Lock, Building2, Map, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";

const SALES = { unit: "dealers", region: "Region", item: "Dealer code", search: "Search dealer code or name…", empty: "No dealer matches.", locked: "Dealer heads see their own dealership" };
export const SERVICE_LABELS = { unit: "workshops", region: "Zone", item: "Workshop code", search: "Search workshop code or name…", empty: "No workshop matches.", locked: "Workshop heads see their own workshop" };

// Cascading scope: Group (or My zone) › Region › Dealer code — one popover, dealers searchable by code or name.
// `labels` swaps the wording for the Service twin (zones / workshops).
export function ScopePicker({ picker, region, dealer, onChange, breadcrumb, labels = SALES }) {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState(null); // region currently expanded in the dealer list
  const root = picker?.root || "Group";
  const regions = picker?.regions || [];
  const dealers = picker?.dealers || [];
  const locked = !!picker?.locked;
  const activeRegion = level ?? (dealer ? dealers.find((d) => d.code === dealer)?.region : region) ?? null;
  const listed = activeRegion && regions.length > 1 ? dealers.filter((d) => d.region === activeRegion) : dealers;
  const pick = (next) => { onChange(next); setOpen(false); setLevel(null); };
  const crumbs = breadcrumb?.length ? breadcrumb : [root];
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setLevel(null); }}>
      <PopoverTrigger asChild>
        <button disabled={locked} className={`inline-flex h-8 items-center gap-1 rounded-md border bg-background px-2 text-xs ${locked ? "cursor-not-allowed opacity-80" : "hover:bg-secondary"}`} title={locked ? labels.locked : "Change scope"} data-testid="ov-scope-trigger">
          {locked ? <Lock size={12} className="text-muted-foreground" /> : <Globe size={12} className="text-muted-foreground" />}
          {crumbs.map((c, i) => <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">{i > 0 && <ChevronRight size={11} className="text-muted-foreground" />}<span className={i === crumbs.length - 1 ? "font-semibold" : "text-muted-foreground"}>{c}</span></span>)}
          {!locked && <ChevronsUpDown size={12} className="ml-1 text-muted-foreground" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start" data-testid="ov-scope-popover">
        <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder={labels.search} data-testid="ov-scope-search" />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>{labels.empty}</CommandEmpty>
            <CommandGroup heading="Scope">
              <CommandItem value={`${root} all ${labels.unit}`} onSelect={() => pick({ region: "", dealer: "" })} data-testid="ov-scope-root" className="text-xs">
                <Globe size={13} className="mr-2 text-muted-foreground" /> {root} <span className="ml-auto text-muted-foreground">{dealers.length} {labels.unit}</span>
              </CommandItem>
            </CommandGroup>
            {regions.length > 1 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={labels.region}>
                  {regions.map((r) => (
                    <CommandItem key={r} value={`region ${r}`} onSelect={() => pick({ region: r, dealer: "" })} className="text-xs" data-testid={`ov-scope-region-${r.replace(/\s+/g, "_")}`}>
                      <Map size={13} className="mr-2 text-muted-foreground" /> {r}
                      <button type="button" onClick={(e) => { e.stopPropagation(); setLevel(level === r ? null : r); }} className="ml-auto rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-secondary" data-testid={`ov-scope-region-expand-${r.replace(/\s+/g, "_")}`}>
                        {dealers.filter((d) => d.region === r).length} {labels.unit} <ChevronRight size={10} className={`inline transition-transform ${level === r ? "rotate-90" : ""}`} />
                      </button>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
            <CommandSeparator />
            <CommandGroup heading={activeRegion && regions.length > 1 ? `${labels.item} · ${activeRegion}` : labels.item}>
              {listed.map((d) => (
                <CommandItem key={d.code} value={`${d.code} ${d.name} ${d.region}`} onSelect={() => pick({ region: d.region, dealer: d.code })} className="text-xs" data-testid={`ov-scope-dealer-${d.code}`}>
                  <Building2 size={13} className="mr-2 text-muted-foreground" /> <span className="font-semibold num">{d.code}</span><span className="ml-1.5">{d.name}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">{d.region}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
